import React, { useEffect, useState } from 'react';
import { getUserCases, getAppUser } from '../../functions/utils';
import {
  getBestandImports,
  getBestandFiles,
  deleteBestandImport,
  handleBestandImport,
  resetBestandStatus,
} from '../../functions/bestand';
import '../../css/ImportComponent.css';
import ImportHeaderComponent from '../page/ImportHeaderComponent';
import DropdownComponent from '../utilities/DropdownComponent';
import LoaderComponent from '../utilities/LoaderComponent';
import FileDropComponent from '../utilities/FileDropComponent';
import ConfirmationComponent from '../utilities/ConfirmationComponent';
import BestandLogo from '../../images/BestandLogo.png';
import { useTranslations } from '../utilities/TranslationContext';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark, faChevronDown, faCircleCheck, faCircleXmark, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faChevronDown, faXmark, faCircleCheck, faCircleXmark, faTriangleExclamation);

const BestandComponent = () => {
  const { getTranslation, language } = useTranslations();

  const [userCases, setUserCases] = useState([]);
  const [imports, setImports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Upload form state
  const [selectedCase, setSelectedCase] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilesMetadata, setPendingFilesMetadata] = useState([]);
  const [pendingJrnid, setPendingJrnid] = useState('');   // BIC[0:4]+IBAN[-4:], e.g. ABNA9999
  const [pendingAccid, setPendingAccid] = useState('');   // IBAN[-4:], editable
  const [pendingYear, setPendingYear] = useState('');     // extracted from file dates
  const [isUploading, setIsUploading] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);

  // Config history expansion
  const [expandedConfigId, setExpandedConfigId] = useState(null);
  const [configFiles, setConfigFiles] = useState({});

  // Confirm delete
  const [confirmDeleteId, setConfirmDeleteId] = useState(false);

  const showUploadForm = pendingFiles.length > 0;
  const canImport = showUploadForm && selectedCase && !isUploading;

  useEffect(() => { init(); }, []);

  async function init() {
    const appUser = await getAppUser();
    if (!appUser) return setTimeout(init, 300);
    const cases = await getUserCases();
    if (cases) setUserCases(cases);
    const impBestanden = await getBestandImports(cases);
    setImports(Array.isArray(impBestanden) ? impBestanden : []);
    setIsLoading(false);
  }

  async function reload() {
    const imps = await getBestandImports(userCases);
    setImports(Array.isArray(imps) ? imps : []);
  }

  // ── File handling ──────────────────────────────────────────────────────────────

  function extractFileMetadata(xml) {
    const ibanMatch = xml.match(/<IBAN>([^<]+)<\/IBAN>/i);
    const bicMatch  = xml.match(/<BIC>([^<]+)<\/BIC>/i);
    const dateMatch = xml.match(/(\d{4})-\d{2}-\d{2}/);
    const iban = ibanMatch ? ibanMatch[1].trim() : '';
    const bic  = bicMatch  ? bicMatch[1].trim()  : '';
    const year = dateMatch ? dateMatch[1] : '';
    const prcdMatch = xml.match(/<Cd>PRCD<\/Cd>[\s\S]*?<Dt><Dt>(\d{4}-\d{2}-\d{2})/);
    const clbdMatch = xml.match(/<Cd>CLBD<\/Cd>[\s\S]*?<Dt><Dt>(\d{4}-\d{2}-\d{2})/);
    const frDt = prcdMatch ? prcdMatch[1] : '';
    const toDt = clbdMatch ? clbdMatch[1] : '';
    const txCount = (xml.match(/<Ntry\b/g) || []).length;
    return { iban, bic, year, frDt, toDt, txCount };
  }

  async function onFilesDropped(files) {
    setPendingFiles(files);
    if (files.length > 0) {
      const metaArr = [];
      for (const file of files) {
        try {
          const xml = await file.text();
          const { bic, iban, year, frDt, toDt, txCount } = extractFileMetadata(xml);
          metaArr.push({
            year,
            jrnid: (bic.slice(0, 4) + iban.slice(-4)).toUpperCase(),
            accid: iban.slice(-4),
            frDt,
            toDt,
            txCount,
          });
        } catch {
          metaArr.push({ year: '', jrnid: '', accid: '', frDt: '', toDt: '', txCount: 0 });
        }
      }
      setPendingFilesMetadata(metaArr);
      if (metaArr.length > 0) {
        setPendingJrnid(metaArr[0].jrnid);
        setPendingYear(metaArr[0].year);
      }
    }
  }

  function cancelUpload() {
    setPendingFiles([]);
    setPendingFilesMetadata([]);
    setPendingJrnid('');
    setPendingYear('');
    setSelectedCase(false);
    setDropdownKey(k => k + 1);
  }

  function removePendingFile(idx) {
    const files = pendingFiles.filter((_, i) => i !== idx);
    const meta = pendingFilesMetadata.filter((_, i) => i !== idx);
    setPendingFiles(files);
    setPendingFilesMetadata(meta);
    if (files.length === 0) {
      setPendingJrnid('');
      setPendingYear('');
    }
  }

  // ── Import ─────────────────────────────────────────────────────────────────────

  async function runImport() {
    if (!selectedCase) return;
    setIsUploading(true);
    await resetBestandStatus();

    const entiteit = `${selectedCase}_1`;
    let configId = null;
    var transactionCount = 0;
    for (const file of pendingFiles) {
      let xmlContent;
      try { xmlContent = await file.text(); }
      catch { continue; }

      // Per-file extraction — fall back to values parsed from the first file
      const { bic, iban, year, txCount } = extractFileMetadata(xmlContent);
      transactionCount+= txCount;
      const jrnid = (bic.slice(0, 4) + iban.slice(-4)).toUpperCase() || pendingJrnid;
      const accid = pendingAccid;
      const fileYear = year || pendingYear;

      try {
        const result = await handleBestandImport({
          entiteit,
          year: fileYear,
          jrnid,
          accid,
          fileName: file.name,
          xmlContent,
        });
        if (result?.configId) configId = result.configId;
      } catch (e) {}
    }
    console.log("Total transactions: " + transactionCount);
    setIsUploading(false);
    cancelUpload();
    await reload();

    if (configId) {
      setExpandedConfigId(configId);
      const files = await getBestandFiles(configId);
      setConfigFiles(prev => ({ ...prev, [configId]: files }));
    }
  }

  // ── Config expansion (file history) ──────────────────────────────────────────

  async function toggleConfig(imp) {
    if (expandedConfigId === imp.id) {
      setExpandedConfigId(null);
    } else {
      setExpandedConfigId(imp.id);
      if (imp.impid > 0 && !configFiles[imp.id]) {
        const files = await getBestandFiles(imp.id);
        setConfigFiles(prev => ({ ...prev, [imp.id]: files }));
      }
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────────

  async function handleDelete(id) {
    await deleteBestandImport(id);
    setConfirmDeleteId(false);
    if (expandedConfigId === id) setExpandedConfigId(null);
    await reload();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  function getCaseName(entiteit) {
    const id = entiteit.substring(0, entiteit.indexOf('_'));
    const c = userCases.find(uc => String(uc.id) === String(id));
    return c ? c.name : entiteit;
  }

  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function formatStamp(stamp) {
    if (!stamp) return '-';
    return new Date(stamp).toLocaleString(`${language?.language_code}-${language?.country_code}`, {
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    });
  }

  return (
    <div className="import-container">
      <ImportHeaderComponent
        title="Bestand"
        logo={BestandLogo}
        subtitle={getTranslation('import_BestandSubtitle')}
      />
      <div className="import-component-container">
        <div className="import-component-content">
          <div className="import-content">

            {/* ── Upload section – always visible ── */}
            <div className="import-create-basic">
              <FileDropComponent onFilesAccepted={onFilesDropped} showFileList={false} />
            </div>

            {/* ── Toggled inputs – only when a file is dropped ── */}
            {showUploadForm && (
              <div className="import-create-basic">
                <div className="case-content">
                  <p>{getTranslation('import_FVCase')}*</p>
                  <DropdownComponent
                    key={dropdownKey}
                    options={userCases.map(c => ({ label: c.name, value: c.id, type: 'case' }))}
                    defaultValue={{ label: getTranslation('import_SelectCase'), value: null, type: null }}
                    onSelect={o => o.type === 'case' && setSelectedCase(o.value)}
                    className="case"
                  />
                </div>
                <div className="administration-content">
                  <p>{getTranslation('import_GLBank')}*</p>
                  <input
                    type="text"
                    className="jaar-input"
                    placeholder="1010"
                    value={pendingAccid}
                    onChange={e => setPendingAccid(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* ── Pending files list ── */}
            {showUploadForm && !isUploading && pendingFiles.length > 0 && (
              <div className="import-results-wrapper">
                <table className="import-results-table">
                  <thead>
                    <tr>
                      <th>Bestandsnaam</th>
                      <th>{getTranslation('import_Year')}</th>
                      <th>Dagboek</th>
                      <th>Periode</th>
                      <th>Transacties</th>
                      <th>Grootte</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingFiles.map((f, i) => {
                      const meta = pendingFilesMetadata[i] || {};
                      const periode = meta.frDt && meta.toDt ? `${meta.frDt} – ${meta.toDt}` : '-';
                      return (
                        <tr key={i} className="file-row-pending">
                          <td>{f.name}</td>
                          <td>{meta.year || '-'}</td>
                          <td>{meta.jrnid || '-'}</td>
                          <td>{periode}</td>
                          <td>{meta.txCount != null ? meta.txCount : '-'}</td>
                          <td>{formatSize(f.size)}</td>
                          <td>
                            <button className="file-drop-item-remove" onClick={() => removePendingFile(i)} type="button">
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Upload loader ── */}
            {isUploading && <LoaderComponent isLoading paddingTop="10px" />}

            {/* ── Menubar ── */}
            {showUploadForm && (
              <div className="import-menubar">
                <button className={`import-btn submit-btn hover${!canImport ? ' disabled' : ''}`} disabled={!canImport} onClick={runImport}>
                  {getTranslation('import_Import')}
                </button>
                <button className="import-btn cancel-btn hover" onClick={cancelUpload}>
                  {getTranslation('import_Cancel')}
                </button>
              </div>
            )}

            {/* ── Config list – always visible ── */}
            <LoaderComponent isLoading={isLoading} paddingTop="20px" />
            {!isLoading && imports.length > 0 && (
              <div className="list-content">
                <table border="1">
                  <tbody>
                    <tr className="list-header">
                      <td className="list-expand"></td>
                      <td className="list-case">{getTranslation('import_Case')}</td>
                      <td className="list-year">{getTranslation('import_Year')}</td>
                      <td>Dagboek</td>
                      <td>Bankrekening</td>
                      <td className="list-stamp no-wrap">{getTranslation('import_LastUpdated')}</td>
                      <td className="edit"></td>
                    </tr>
                    {imports.map((imp, index) => {
                      const isExpanded = expandedConfigId === imp.id;
                      return (
                        <React.Fragment key={imp.id}>
                          <tr value={index} onClick={() => toggleConfig(imp)}>
                            <td className="td-expand">
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`config-expand-icon${isExpanded ? ' rotated' : ''}`}
                              />
                            </td>
                            <td>{getCaseName(imp.entiteit)}</td>
                            <td>{imp.year}</td>
                            <td>{imp.jrnid}</td>
                            <td>{imp.accid}</td>
                            <td>{formatStamp(imp.stamp)}</td>
                            <td className="td-editor">
                              <div className="delete-list list-editor-option hover" onClick={e => { e.stopPropagation(); setConfirmDeleteId(imp.id); }}>
                                <FontAwesomeIcon icon={faXmark} />
                              </div>
                              {confirmDeleteId === imp.id && (
                                <ConfirmationComponent
                                  message="Weet u zeker dat u deze configuratie wilt verwijderen?"
                                  onCancel={() => setConfirmDeleteId(false)}
                                  onConfirm={() => handleDelete(imp.id)}
                                />
                              )}
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr className="config-files-row">
                              <td colSpan={7}>
                                {(() => {
                                  const existing = configFiles[imp.id] ?? [];
                                  if (imp.impid === 0 || existing.length === 0) {
                                    return <p style={{ padding: '8px' }}>Geen bestanden geïmporteerd.</p>;
                                  }
                                  return (
                                    <div className="import-results-wrapper">
                                      <table className="import-results-table">
                                        <thead>
                                          <tr>
                                            <th></th>
                                            <th>Bestandsnaam</th>
                                            <th>Type</th>
                                            <th>Periode</th>
                                            <th>Transacties</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {existing.map(f => (
                                            <tr key={f.id} className="file-row-existing">
                                              <td><FontAwesomeIcon icon={faCircleCheck} /></td>
                                              <td>{f.file_name}</td>
                                              <td>{f.file_type}</td>
                                              <td>{f.opening_balance_date && f.closing_balance_date ? `${f.opening_balance_date} – ${f.closing_balance_date}` : '-'}</td>
                                              <td>{f.transaction_count}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default BestandComponent;
